import {
    AppShortcut,
    EntityKey,
    EntityType,
    RemoteJobExpectedArgumentRepresentation,
    RemoteJobPredefinedActionRepresentation,
} from '@common/api';
import { Button } from '@components/button';
import { ContextMenu, useContextMenuState } from '@components/context-menu/ContextMenu';
import { Delete, Play, Plus, Refresh } from '@components/icons/FarseerIcons';
import { SelectInputField } from '@components/form';
import { Modal } from '@components/modal/Modal';
import { useModal } from '@components/modal/ModalProvider';
import { useRemoteJobActions, useRemoteJobsByIds } from '@data/hooks/remoteJobs';
import { GlobalSearchModal } from '@features/global-search/GlobalSearchModal';
import { areActionArgumentsValid, RunJobExpectedArgumentsModal } from '@features/remoteJobs/RemoteJobDetails';
import { useI18nContext } from '@i18n/i18n-react';
import { useAuth } from '@providers/AuthProvider';
import { CSSProperties, useRef } from 'react';
import toast from 'react-hot-toast';
import { useSheetEditorContext } from '../editor/SheetEditorProvider';

export function AppShortcuts({
    buttonType = 'quaternary',
    buttonLabel,
    buttonStyle,
}: {
    buttonType?: 'quaternary' | 'primary' | 'secondary' | 'tertiary' | 'inverted' | undefined;
    buttonLabel?: string;
    buttonStyle?: CSSProperties;
}) {
    const { LL } = useI18nContext();
    const menuTriggerRef = useRef<HTMLButtonElement>(null);
    const { menuState, menuHandlers } = useContextMenuState({ click: 'left' });
    const { hideModal, showModal } = useModal();
    const { user } = useAuth();
    const {
        state: {
            sheetConfig: { appShortcuts },
            canEditSheet,
        },
        api: { updateSheet },
    } = useSheetEditorContext();
    const { runRemoteJob } = useRemoteJobActions();
    const defaultAppAction = LL.RUN_JOB();
    const displayedShortcuts: { id: number; name: string; chosenAction: string; availableActions: string[] }[] = [];
    const remoteJobs = useRemoteJobsByIds(appShortcuts.map((as) => as.id));
    const remoteJobData: Map<
        number,
        {
            predefinedActions: RemoteJobPredefinedActionRepresentation[] | undefined;
            expectedArguments: RemoteJobExpectedArgumentRepresentation[];
        }
    > = new Map();

    remoteJobs.forEach((jobQuery, index) => {
        if (!jobQuery.isLoading && !jobQuery.isError) {
            const id = appShortcuts[index].id;
            remoteJobData.set(id, {
                predefinedActions: jobQuery.data.predefinedActions,
                expectedArguments: jobQuery.data.expectedArguments,
            });
            const availableActions = jobQuery.data.predefinedActions?.map((action) => action.name) || [];
            displayedShortcuts.push({
                id,
                name: jobQuery.data?.name || '',
                chosenAction: findChosenAction(id, availableActions),
                availableActions,
            });
        }
    });

    const hasFullEditPermission = canEditSheet && user?.classActions.sheetClassActions.canUpdate;
    const hasRequiredRemoteJobPermissions =
        user?.classActions.remoteJobClassActions.canRun && user?.classActions.remoteJobClassActions.canGetById;
    const canSeeShortcuts = hasRequiredRemoteJobPermissions && (hasFullEditPermission || displayedShortcuts.length > 0);

    function findChosenAction(id: number, availableActions: string[]): string {
        const appShortcut = appShortcuts.find((as) => as.id === id);
        const savedAction = appShortcut?.action;
        if (!savedAction) {
            return defaultAppAction;
        }

        if (availableActions.includes(savedAction)) {
            return savedAction;
        } else {
            appShortcut.action = '';
            return defaultAppAction;
        }
    }

    function onAddShortcut() {
        showModal(
            <GlobalSearchModal
                handleSidebarSelect={async (key: EntityKey) => {
                    const newAppShortcut: AppShortcut = {
                        id: key.id,
                        action: '',
                    };

                    if (appShortcuts.some((as) => as.id === key.id)) {
                        toast.error(LL.SHORTCUT_ALREADY_EXISTS());
                        return;
                    }

                    removeDeletedAppShortcuts();
                    await updateSheet({ appShortcuts: [...appShortcuts, newAppShortcut] });
                    hideModal();
                    toast.success(LL.SHORTCUT_ADDED());
                }}
                filters={[EntityType.RemoteJob]}
                displaySuggestions={true}
            />
        );
    }

    function onChangeActionRequest(id: number, actions: string[], currentSelectedAction: string) {
        const actionOptions: { label: string; value: string }[] = actions.map((action) => ({
            label: action,
            value: action,
        }));
        let newAction = currentSelectedAction == defaultAppAction ? '' : currentSelectedAction;
        const handleChosenActionChange = async (action: string) => {
            newAction = action == defaultAppAction ? '' : action;
        };
        const handleChosenActionSave = async () => {
            removeDeletedAppShortcuts();
            const chosenAppIndex = appShortcuts.findIndex((as) => as.id === id);
            const modifiedShortcuts = appShortcuts.filter((as) => as.id !== id);
            modifiedShortcuts.splice(chosenAppIndex, 0, { id, action: newAction });
            await updateSheet({ appShortcuts: modifiedShortcuts });
            toast.success(LL.ACTION_CHANGED());
            hideModal();
        };

        showModal(
            <>
                <Modal.Content width={400}>
                    <Modal.Header title={LL.CHANGE_ACTION_TITLE()} />
                    <Modal.Body>
                        <SelectInputField
                            label={LL.CHANGE_ACTION_LABEL()}
                            value={actionOptions[0].value}
                            onChange={handleChosenActionChange}
                            options={actionOptions}
                            placeholder={currentSelectedAction}
                        />
                    </Modal.Body>
                    <Modal.Footer done={handleChosenActionSave} />
                </Modal.Content>
            </>
        );
    }

    async function onRemoveShortcut(id: number) {
        removeDeletedAppShortcuts();
        const modifiedShortcuts = appShortcuts.filter((as) => as.id !== id);
        await updateSheet({ appShortcuts: [...modifiedShortcuts] });
        toast.success(LL.SHORTCUT_REMOVED());
    }

    function removeDeletedAppShortcuts() {
        for (let i = appShortcuts.length - 1; i >= 0; i--) {
            const appShortcut = appShortcuts[i];
            if (!displayedShortcuts.some((ds) => ds.id === appShortcut.id)) {
                appShortcuts.splice(i, 1);
            }
        }
    }

    function onRunShortcut(id: number, action: string) {
        const chosenJobData = remoteJobData.get(id);

        if (!chosenJobData) {
            toast.error(LL.ERROR_FETCHING_APP_DATA());
            return;
        }

        const { expectedArguments, predefinedActions } = chosenJobData;
        const actionArgs = predefinedActions?.find((pa) => pa.name === action);
        const isDefaultAction = action === defaultAppAction;

        if (expectedArguments && expectedArguments.length > 0) {
            if (isDefaultAction) {
                showModal(
                    <RunJobExpectedArgumentsModal
                        expectedArguments={expectedArguments}
                        onConfirm={(jobArguments) => startRemoteJob(id, jobArguments)}
                    />
                );
                return;
            }

            if (!actionArgs?.runArguments) {
                toast.error(LL.ERROR_FETCHING_ACTION_ARGUMENTS());
                return;
            }

            if (areActionArgumentsValid(expectedArguments, actionArgs.runArguments)) {
                startRemoteJob(
                    id,
                    expectedArguments.map((arg) => actionArgs.runArguments[arg.name])
                );
            } else {
                toast.error(LL.INVALID_ACTION_ARGUMENTS());
            }
        } else {
            startRemoteJob(id, []);
        }
    }

    function startRemoteJob(remoteJobId: number, jobArguments: string[]) {
        runRemoteJob({ remoteJobId, jobArguments });
        toast.success(LL.APP_STARTED_SUCCESSFULLY());
    }

    return (
        <>
            {canSeeShortcuts ? (
                <>
                    <Button
                        $size="small"
                        $type={buttonType}
                        title={buttonLabel}
                        icon={<Play />}
                        style={buttonStyle}
                        ref={menuTriggerRef}
                        {...menuHandlers}
                    />
                    <ContextMenu {...menuState} anchorRef={menuTriggerRef} placement="bottom-start" noFocus={true}>
                        <ContextMenu.Path>{LL.APP_SHORTCUTS_PATH()}</ContextMenu.Path>
                        {displayedShortcuts.map((item, index) => (
                            <ContextMenu.Item
                                key={index}
                                item={{
                                    icon: Play,
                                    label: `${item.name} (${item.chosenAction})`,
                                    action: () => onRunShortcut(item.id, item.chosenAction),
                                }}
                                overrideNoFocusValue={false}
                            >
                                {hasFullEditPermission ? (
                                    <>
                                        <ContextMenu.Item
                                            item={{
                                                icon: Refresh,
                                                label: LL.CHANGE_SHORTCUT_ACTION(),
                                                action: () => {
                                                    onChangeActionRequest(
                                                        item.id,
                                                        [defaultAppAction, ...item.availableActions],
                                                        item.chosenAction
                                                    );
                                                },
                                            }}
                                        />
                                        <ContextMenu.Item
                                            item={{
                                                icon: Delete,
                                                label: LL.REMOVE_SHORTCUT(),
                                                action: async () => {
                                                    await onRemoveShortcut(item.id);
                                                },
                                            }}
                                        />
                                    </>
                                ) : null}
                            </ContextMenu.Item>
                        ))}
                        {hasFullEditPermission ? (
                            <>
                                <ContextMenu.Divider key="template-divider" />
                                <ContextMenu.Item
                                    item={{
                                        icon: Plus,
                                        label: LL.ADD_APP_SHORTCUT(),
                                        action: onAddShortcut,
                                    }}
                                />
                            </>
                        ) : null}
                    </ContextMenu>
                </>
            ) : null}
        </>
    );
}
